import React, { useState } from "react";
import { Button, IconButton } from "@mui/material";
import Box from "../mui/Box";
import Typography from "../mui/Typography";
import FileUploader from "../common/FileUploader";
import ImageComponent from "../mui/Image";
import CentreTable from "./CentreTable";
import * as XLSX from "xlsx";
import { useImportCentreMutation } from "../../store/apis/centerApis"; // Import the mutation hook
import { toaster } from "../../helper/toaster";
import Loader from "../common/Loader";

interface ImportDialogContentProps {
  onClose: (
    event: React.MouseEvent | {},
    reason: "backdropClick" | "escapeKeyDown"
  ) => void;
  onSuccess: () => void; // Prop to notify success in the parent
}
interface SheetHeader {
  keyName: string;
  label: string;
  sheetDataIndex: number;
  width?: number | string;
  editable?: boolean;
}
const ImportCenterContent: React.FC<ImportDialogContentProps> = ({
  onClose,
  onSuccess,
}) => {
  const [sheetData, SetSheetData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<any>([]);
  const [centerTableModal, setCenterTableModal] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [isSavedButtonEnabled, setIsSavedButtonEnabled] = useState({});
  const [errorMessages, setErrorMessages] = useState<Record<number, string>>(
    {}
  );

  const [importCentre, { isLoading, isError, error, isSuccess }] =
    useImportCentreMutation(); // Hook to trigger the API
  const keyNameAsPerFile = ["region"];
  const handleFileChange = (value: { name: string; value: File[] | null }) => {
    if (value.value && value.value.length > 0) {
      setFile(value.value[0]);
    }
  };

  const handleFileUnselect = () => {
    setFile(null);
    SetSheetData([]);
    setHeaders([]);
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "assets/images/login/DummyFiles/import_center_format.xlsx";
    link.setAttribute("download", "import_center_format.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSheetHeader = () => {
    return [
      {
        keyName: "region",
        label: "Region",
        sheetDataIndex: 0,
        width: 180,
        editable: true,
      },
      {
        keyName: "centerId",
        label: "#",
        sheetDataIndex: 1,
        width: 180,
        editable: true,
      },
      {
        keyName: "centerName",
        label: "SC Center name ",
        sheetDataIndex: 2,
        width: 180,
        editable: true,
      },
      {
        keyName: "biometricDeviceId",
        label: "Biometric device id",
        sheetDataIndex: 3,
        width: 180,
        editable: true,
      },
      {
        keyName: "status",
        label: "Status",
        sheetDataIndex: 4,
        width: 180,
        editable: true,
      },
      {
        keyName: "funderName",
        label: "Funder",
        sheetDataIndex: 5,
        width: 180,
        editable: true,
      },
      {
        keyName: "district",
        label: "District",
        sheetDataIndex: 6,
        width: 180,
        editable: true,
      },
      {
        keyName: "state",
        label: "State",
        sheetDataIndex: 7,
        width: 180,
        editable: true,
      },
      {
        keyName: "genderType",
        label: "Gender type",
        sheetDataIndex: 8,
        width: 180,
        editable: true,
      },
      {
        keyName: "centerBusinessType",
        label: "Centre business type",
        sheetDataIndex: 9,
        width: 180,
        editable: true,
      },
      {
        keyName: "centerStatus",
        label: "Physical/Virtual Status",
        sheetDataIndex: 10,
        width: 180,
        editable: true,
      },
      {
        keyName: "programType",
        label: "Programme Type",
        sheetDataIndex: 11,
        width: 180,
        editable: true,
      },
      {
        keyName: "mcpCenter",
        label: "MCP Centre",
        sheetDataIndex: 12,
        width: 180,
        editable: true,
      },
      {
        keyName: "nationalDirectorName",
        label: "National Sr. Director",
        sheetDataIndex: 13,
        width: 180,
        editable: true,
      },
      {
        keyName: "nationalDirectorEmail",
        label: "National Director Email id",
        sheetDataIndex: 14,
        width: 180,
        editable: true,
      },
      {
        keyName: "regionalDirectorName",
        label: "Regional Director",
        sheetDataIndex: 15,
        width: 180,
        editable: true,
      },
      {
        keyName: "regionalDirectorEmail",
        label: "RD Email id",
        sheetDataIndex: 16,
        width: 180,
        editable: true,
      },
      {
        keyName: "cityManagerName",
        label: "City Manager",
        sheetDataIndex: 17,
        width: 180,
        editable: true,
      },
      {
        keyName: "cityManagerEmail",
        label: "CM Email id",
        sheetDataIndex: 18,
        width: 180,
        editable: true,
      },
      {
        keyName: "clusterManagerName", // Key not found in Api body
        label: "Cluster Manager Name",
        sheetDataIndex: 19,
        width: 180,
        editable: true,
      },

      {
        keyName: "clusterManagerEmailId", // key not found in Api body
        label: "Cluster Manager Email id",
        sheetDataIndex: 20,
        width: 180,
        editable: true,
      },
      {
        keyName: "centerManagerName",
        label: "Centre Manager Name",
        sheetDataIndex: 21,
        width: 180,
        editable: true,
      },
      {
        keyName: "centerManagerEmail",
        label: "Centre Manager Email id",
        sheetDataIndex: 22,
        width: 180,
        editable: true,
      },

      {
        keyName: "placementHeadName",
        label: "CRM/SPOC",
        sheetDataIndex: 23,
        width: 180,
        editable: true,
      },
      {
        keyName: "placementHeadEmail",
        label: "CRM/SPOC Email id",
        sheetDataIndex: 24,
        width: 180,
        editable: true,
      },
      {
        keyName: "regionalDataManagerName",
        label: "Regional Data Manager",
        sheetDataIndex: 25,
        width: 180,
        editable: true,
      },
      {
        keyName: "regionalDataManagerEmail",
        label: "RDM Email id",
        sheetDataIndex: 26,
        width: 180,
        editable: true,
      },
      // {
      //   keyName: "projectCode",
      //   label: "Project Code",
      //   sheetDataIndex: 27,
      //   width: 180,
      //   editable: true,
      // },
      // {
      //   keyName: "city",
      //   label: "City",
      //   sheetDataIndex: 28,
      //   width: 180,
      //   editable: true,
      // },
      // {
      //   keyName: "districtLevelManagerName",
      //   label: "District Level Manager Name",
      //   sheetDataIndex: 29,
      //   width: 180,
      //   editable: true,
      // },
    ];
  };

  // Removed unnecessary comments and optimized the handleImportFiles function
  const handleImportFiles = () => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        const json = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        const cleanedData = json.filter((row: any) =>
          row.some((cell: any) => cell !== "")
        );

        if (cleanedData.length > 0) {
          const excelHeaders = cleanedData[0] as string[]; // Headers from Excel file
          const headers = getSheetHeader(); // Predefined header configuration

          // Create a mapping from Excel headers to predefined headers
          const headerMap: any = {};
          excelHeaders.forEach((excelHeader, index) => {
            const matchedHeader = headers.find(
              (header) =>
                header.label.toLowerCase() === excelHeader.toLowerCase()
            );
            headerMap[index] = matchedHeader ? matchedHeader.keyName : null;
          });

          // Prepare the data rows
          const dataArray = cleanedData.slice(1).map((row: any, i: number) => {
            const rowObject: { [key: string]: any } = { id: i, errorMsg: "" };
            row.forEach((cell: any, index: number) => {
              const keyName = headerMap[index];
              rowObject[keyName || `unknown_${index}`] = keyName ? cell : "-";
            });
            return rowObject;
          });

          setHeaders(headers);
          SetSheetData(dataArray);
          setCenterTableModal(true);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleDialogClose = () => {
    setCenterTableModal(false);
  };

  // Prepare data and submit it to the API
  const handleSubmit = async (tabelData: any) => {
    // Check if any row is in "edit" mode
    const hasUnsavedChanges = Object.entries(isSavedButtonEnabled).some(
      ([rowId, modeObject]: any) => {
        if (modeObject.mode === "edit") {
          toaster.info("Changes detected! Don't forget to save.");
          return true;
        }
        return false;
      }
    );

    if (hasUnsavedChanges) {
      return;
    }
    try {
      const payload = {
        records: sheetData.map((row) => {
          // Map the sheetData object to the API payload structure
          return {
            centerId: row?.centerId || "",
            centerName: row?.centerName || "",
            genderType: row?.genderType || "",
            // projectCode: row["SC Center name "] || "",
            region: row?.region || "",
            state: row?.state || "",
            district: row?.district || "",
            // city: row["District"] || "",
            centerStatus: row?.centerStatus || "",
            funderName: row?.funderName || "",
            mcpCenter: row?.mcpCenter == "Yes",
            programType: row?.programType || "",
            // programSubType: row["Centre business type"] || "",
            centerBusinessType: row?.centerBusinessType || "",
            biometricDeviceId: row?.biometricDeviceId || "",
            regionalDirectorEmail: row?.regionalDirectorEmail || "",
            regionalDirectorName: row?.regionalDirectorName || "",
            // regionalDirectorPhoneNo: row["Regional Director"] || "",
            cityManagerEmail: row?.cityManagerEmail || "",
            cityManagerName: row?.cityManagerName || "",
            // cityManagerPhoneNo: row["City Manager"] || "",
            districtLevelManagerEmail: row?.districtLevelManagerEmail || "" || "",
            districtLevelManagerName: row?.districtLevelManagerName || "" || "",
            // districtLevelManagerPhoneNo: row["Regional Data Manager"] || "",
            clusterManagerEmail: row?.clusterManagerEmailId || "",
            clusterManagerName: row?.clusterManagerName || "",
            centerManagerName:row?.centerManagerName || "",
            centerManagerEmail:row?.centerManagerEmail || "" || "",
            // clusterManagerPhoneNo: row["Cluster Manager"] || "",
            regionalDataManagerEmail: row?.regionalDataManagerEmail || "",
            regionalDataManagerName: row?.regionalDataManagerName || "",
            // regionalDataManagerPhoneNo: row["Regional Data Manager"] || "",
            placementHeadEmail: row?.placementHeadEmail || "",
            placementHeadName: row?.placementHeadName || "",
            // placementHeadPhoneNo: row["RDM Email id"] || "",
            nationalDirectorEmail: row?.nationalDirectorEmail || "",
            nationalDirectorName: row?.nationalDirectorName || "",
            // nationalDirectorPhoneNo: row["National Director"] || "",
            // projectCode: row?.projectCode.toString(),
            // city: row?.city || "",
          };
        }),
      };

      // Call the importCentre mutation
      setUploading(true);
      const response = await importCentre(payload).unwrap();

      // Handle success (response)
      if (response.isCreated) {
        onSuccess();
        setCenterTableModal(false);
      }
    } catch (err: any) {
      const newErrorMessages: any = {};

      // Check if err.data is a single string message
      if (typeof err.data === "string") {
        const match = err.data.match(/records\[(\d+)\]\.(\w+)/);

        if (match) {
          const [_, rowIndex, field] = match;
          newErrorMessages[+rowIndex] = `Error in ${field}: ${err.data}`;
        }
      }
      // Check if err.data is an array of messages
      else if (Array.isArray(err.data)) {
        err.data.forEach((error: string) => {
          const match = error.match(/records\[(\d+)\]\.(\w+)/);

          if (match) {
            const [_, rowIndex, field] = match;
            newErrorMessages[+rowIndex] = `Error in ${field}: ${error}`;
          }
        });
      }

      setErrorMessages(newErrorMessages);
      console.error("Error importing center:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (rowIndex: number, updatedRow: any) => {
    const updatedData = [...sheetData];
    updatedData[rowIndex] = updatedRow;
    SetSheetData(updatedData);
  };

  const handleDelete = (rowIndex: number) => {
    const updatedData = sheetData.filter((_, index) => index !== rowIndex);
    SetSheetData(updatedData);
  };

  return (
    <Box sx={{ padding: "24px", paddingTop: "12px" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: "18px" }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
          Import
        </Typography>
        <IconButton onClick={(e) => onClose(e, "escapeKeyDown")}>
          <ImageComponent
            sx={{ height: "24px", width: "24px" }}
            src="assets/images/login/crossIcon.svg"
            alt="Close Icon"
          />
        </IconButton>
      </Box>

      <FileUploader
        handleChange={handleFileChange}
        handleFileUnselect={handleFileUnselect}
        acceptedFileTypes={{
          "text/csv": [".csv"],
          "application/vnd.ms-excel": [".xls"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
          ],
        }}
        multipleFiles={false}
        name="fileUpload"
        onDownloadTemplate={handleDownloadTemplate}
      />

      <Box
        display="flex"
        justifyContent="space-between"
        sx={{ paddingTop: "28px", gap: "12px" }}
      >
        <Button
          onClick={(e) => onClose(e, "escapeKeyDown")}
          variant="outlined"
          color="secondary"
          sx={{
            width: "287px",
            backgroundColor: "#FFF",
            color: "#082648",
            border: "1px solid #082648",
            textTransform: "capitalize",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={handleImportFiles}
          sx={{
            width: "287px",
            backgroundColor: "#FFF1B7",
            border: "1px solid #997906",
            color: "#082648",
            textTransform: "capitalize",
          }}
        >
          Import
        </Button>
      </Box>

      {/* Pass the edit and delete handlers to CentreTable */}
      <CentreTable
        open={centerTableModal}
        onClose={handleDialogClose}
        headers={headers}
        sheetData={sheetData}
        onSubmit={handleSubmit}
        onEdit={handleEdit}
        onDelete={handleDelete}
        uploading={uploading}
        isSaveButtonEnabled={setIsSavedButtonEnabled}
        errorMessages={errorMessages}
        isLoading={isLoading}
        setSheetData={SetSheetData}
      />
    </Box>
  );
};

export default ImportCenterContent;
